"use client";

import { FeatureHeroSection } from "@/components/shared/feature_hero_section";
import { AlternatingSection } from "@/components/shared/alternating_section";
import { DynamicEvents } from "@/components/shared/dynamic_events";
import { get__related_how_we_help_pages } from "@/cms/how-we-help-data";
import { useTranslations } from "next-intl";

const AdaptiveLearning = () => {
    const t = useTranslations();

    return (
        <main className="flex justify-center" role="main" aria-label="StudyStash Adaptive Learning Feature">
            <div className="flex w-full flex-col">
                {/* Hero Section */}
                <FeatureHeroSection
                    icon="/icon-pack/StudyStash - Icon - Graph.png"
                    iconAlt="Adaptive Learning Icon"
                    category="adaptive learning"
                    title="Adaptive learning intelligence "
                    highlightedText="For students, by students"
                    description="StudyStash recognises that a student struggling with cardiac physiology needs different support than one mastering it effortlessly. We adjust difficulty, timing, and format in seconds."
                    buttonText="Join the pilot"
                    buttonHref="/pilot-program"
                    secondButtonText="See it in practice"
                    secondButtonHref="/book-a-demo"
                    showDemoModal={false}
                    backgroundImage="/apple_images.png"
                    backgroundImageAlt="StudyStash Adaptive Learning Dashboard"
                    mobileImage="/apple_image_mobile.png"
                    mobileImageAlt="StudyStash Adaptive Learning Dashboard"
                />

                {/* Alternating Sections */}
                <div className="pt-32 md:pt-48 lg:pt-96">
                    <AlternatingSection 
                        sections={[
                            {
                            subtitle: "True adaptive learning",
                                title: "StudyStash learns as you learn",
                                content: (
                                    <>
                                        <p className="mb-4">
                                            The more you use StudyStash, the more it learns about you, zeroing in one concepts you struggle with and creating a {t("general.words.personalised")} model about each student.
                                        </p>
                                        <p>
                                            Each learning mode knows your learning areas so actively creates content and schedules / shuffles it in a way that you focus on the areas we know you need more support with.
                                        </p>
                                    </>
                                ),
                                video: {
                                    src: "/how-we-help/neuroscience/neuroscience-zeros-in.gif",
                                    alt: "Demonstration of spaced repetition learning mode",
                                    type: "gif"
                                }
                            },
                            {
                                subtitle: "Always-on study support",
                                title: "Adapted to your learning schedule",
                                content: (
                                  <>
                                    <p className="mb-4">
                                      <span className="font-semibold">Smart scheduling:</span> StudyStash organises your revision around upcoming exams, making sure you see the right content at the right time so knowledge is reinforced when you need it most.
                                    </p>
                                    <p>
                                      <span className="font-semibold pt-4">Right level of challenge:</span> Content even adapts to your progress, adjusting the difficulty to keep you in that ideal zone where learning feels achievable but still pushes you forward.
                                    </p>
                                  </>                              
                                ),
                                video: {
                                    src: "/how-we-help/neuroscience/calendar_reminders.gif",
                                    alt: "Demonstration of adaptive learning features",
                                    containerClassName: "rounded-lg bg-green-200 px-4 pt-6 shadow-xl md:px-10 md:pt-10",
                                    type: "gif"
                                }
                            },
                            {
                                subtitle: "Feedback that saves time",
                                title: `Instant ${t("general.words.personalised")} feedback`,
                                content: (
                                  <p>
                                    StudyStash gives students specific feedback in seconds, targeting the exact areas it has tracked them struggling with. 
                                    This ensures students get timely guidance while also supporting faculty who may not always have the capacity to provide one-to-one feedback.
                                  </p>
                                ),
                                video: {
                                  src: "/videos/confused_students.mp4",
                                  alt: "Students receiving instant feedback",
                                  containerClassName: "rounded-lg bg-ss-blue/20 px-4 pt-6 shadow-xl md:px-10 md:pt-10"
                                }
                            }
                        ]}
                    />
                </div>

                {/* Related How We Help Pages */}
                <DynamicEvents 
                    items={get__related_how_we_help_pages("/how-we-help/adaptive-learning")}
                    showViewAll={false}
                    showKeepReading={true}
                />
            </div>
        </main>
    );
};

export default AdaptiveLearning;